import {
  type MFELoaderErrorEvent,
  type TMFELoaderElement,
} from "@sinch/mfe-loader";
import { useEffect, useMemo, useRef, useState } from "react";
import CrashPage from "../../ErrorPages/CrashPage";
import { useIsMobile } from "../../hooks/useIsMobile";
import type { RemoteName } from "../../mfe/mfeConfig";
import { useTimeToFirstMfeLoadRouterTrace } from "../../sentry/hooks";
import { getMountId } from "../../utils";
import { useCurrentMfe } from "./state";
import type { MfeRouteConfig } from "./types";

interface RouterProps {
  config: Record<RemoteName, MfeRouteConfig>;
  LoadingScreen?: React.ComponentType;
  forceMfeScroll?: boolean;
}

export const Router = ({
  config,
  LoadingScreen,
  forceMfeScroll = true,
}: RouterProps) => {
  const isMobile = useIsMobile();
  const currentMfe = useCurrentMfe(config);
  const [{ loading, error }, setState] = useState({
    loading: true,
    error: null as unknown | null,
  });
  // importElement is used for MFEs owned by the shell
  const importElementRef = useRef<HTMLDivElement | null>(null);
  const importElementUnmountFnRef = useRef<undefined | (() => void)>(undefined);
  const mfeLoaderRef = useRef<TMFELoaderElement>(null);

  const onMfeLoad = useTimeToFirstMfeLoadRouterTrace(currentMfe);

  useEffect(() => {
    setState({ loading: true, error: null });
    let isConnected = true;
    const importElement = importElementRef.current;

    if (!currentMfe || !currentMfe.import || importElement === null) {
      setState({ loading: false, error: null });
      return;
    }

    const mountId = getMountId(currentMfe);
    const mountElement = document.createElement("div");
    importElement.appendChild(mountElement);

    currentMfe
      .import()
      .then((mfeMountFn) => {
        if (!isConnected || mountId !== getMountId(currentMfe)) return;
        importElementUnmountFnRef.current = mfeMountFn(mountElement, {
          basePath: currentMfe.path.replace(/\/\*$/, ""),
        });
        setState({ loading: false, error: null });
      })
      .catch((err) => {
        if (!isConnected || mountId !== getMountId(currentMfe)) return;
        setState({ loading: false, error: err });
      });

    return () => {
      isConnected = false;
      if (importElementUnmountFnRef.current) {
        importElementUnmountFnRef.current();
        importElementUnmountFnRef.current = undefined;
      }
      if (importElement && importElement.firstChild) {
        importElement.removeChild(importElement.firstChild);
      }
    };
  }, [currentMfe]);

  const handleLoad = () => {
    const mountId = currentMfe ? getMountId(currentMfe) : "";
    onMfeLoad(mountId);
  };

  const handleError = (e: CustomEvent<MFELoaderErrorEvent>) => {
    setState({ loading: false, error: e.detail.originalError });
  };

  const handleMount = () => {
    setState({ loading: false, error: null });

    if (forceMfeScroll) {
      // The Shell should not be responsible for styling MFE's mount container,
      // this code can safely be removed when all MFEs that are rendered
      // in this Router has set the mount div to overflow: auto
      const mountElement = mfeLoaderRef.current?.mountElement;
      if (mountElement) {
        if (!mountElement.style.overflow) {
          Object.assign(mountElement.style, { overflow: "auto" });
        }
      } else {
        console.error("Mount element not found, this should not happen!");
        if (mfeLoaderRef.current) {
          mfeLoaderRef.current.style.overflow = "auto";
        }
      }
    }
  };

  const isShowingLoading = loading && LoadingScreen;
  const style = useMemo(
    () => ({
      height: "100%",
      width: isMobile ? "100vw" : "100%",
      display: isShowingLoading ? "none" : "flex",
      flexDirection: "column" as const,
    }),
    [isMobile, isShowingLoading]
  );

  return (
    <>
      {isShowingLoading && <LoadingScreen />}
      {error && <CrashPage error={error} />}
      {currentMfe?.import && <div style={style} ref={importElementRef} />}
      {!currentMfe?.import && currentMfe?.name && (
        <mfe-loader
          style={style}
          ref={mfeLoaderRef}
          name={currentMfe.name}
          module={currentMfe.entry}
          base-path={currentMfe.path}
          on-load={handleLoad}
          on-error={handleError}
          on-mount={handleMount}
        />
      )}
    </>
  );
};
